import { type z } from 'zod';

import { type ProblemDetails, problemDetailsSchema } from '@fiscoai/contracts';

import { sessao } from './auth/sessao';

/** Erro de API com o corpo RFC 9457 já validado. */
export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  get status(): number {
    return this.problem.status;
  }
}

type ValorQuery = string | number | boolean | readonly string[] | null | undefined;

export interface RequestOptions<TSchema extends z.ZodType> {
  /** Schema da resposta: o corpo é validado em runtime, não só tipado. */
  schema: TSchema;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Objeto vira JSON; `FormData` vai como multipart (o navegador põe o boundary). */
  body?: unknown;
  query?: Record<string, ValorQuery>;
  signal?: AbortSignal;
  /** `false` em rotas públicas (login): não envia token nem tenta renovar. */
  auth?: boolean;
}

export const API_BASE = '/api/v1';

/** Monta a URL com a query; listas viram CSV (`empresaIds=a,b`), vazios são omitidos. */
export function montarUrl(caminho: string, query: Record<string, ValorQuery> = {}): string {
  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(query)) {
    if (valor === undefined || valor === null || valor === '') continue;
    if (Array.isArray(valor)) {
      if (valor.length > 0) params.set(chave, valor.join(','));
      continue;
    }
    params.set(chave, String(valor));
  }
  const texto = params.toString();
  return `${API_BASE}${caminho}${texto ? `?${texto}` : ''}`;
}

/**
 * Cliente HTTP único da interface. Com `auth`, envia o access token; num 401 renova a sessão
 * UMA vez e repete — se não der, a sessão acabou e a interface volta ao login.
 */
export async function request<TSchema extends z.ZodType>(
  caminho: string,
  options: RequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  const auth = options.auth ?? true;
  if (auth && !sessao.tokenAtual() && sessao.temSessao()) await sessao.renovar();

  let resposta = await enviar(caminho, options, auth);
  if (auth && resposta.status === 401) {
    if (await sessao.renovar()) resposta = await enviar(caminho, options, auth);
    else sessao.avisarExpirada();
  }
  return interpretar(resposta, options.schema);
}

function enviar<TSchema extends z.ZodType>(
  caminho: string,
  { method = 'GET', body, query, signal }: RequestOptions<TSchema>,
  auth: boolean,
): Promise<Response> {
  const token = auth ? sessao.tokenAtual() : null;
  const multipart = body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(montarUrl(caminho, query), {
    method,
    credentials: 'same-origin',
    headers,
    ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });
}

async function interpretar<TSchema extends z.ZodType>(
  resposta: Response,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  const payload: unknown =
    resposta.status === 204 ? undefined : await resposta.json().catch(() => undefined);
  if (!resposta.ok) {
    const problema = problemDetailsSchema.safeParse(payload);
    throw new ApiError(
      problema.success
        ? problema.data
        : { type: 'about:blank', title: resposta.statusText || 'Erro', status: resposta.status },
    );
  }
  return schema.parse(payload);
}

/** Busca um arquivo autenticado (Bearer em memória — não dá para usar `<a href>`). */
async function buscarArquivo(
  caminho: string,
  query: Record<string, ValorQuery> = {},
): Promise<{ blob: Blob; nome: string | null }> {
  // Mesmo ciclo do request(): renova se há sessão sem token, e de novo no 401.
  if (!sessao.tokenAtual() && sessao.temSessao()) await sessao.renovar();
  const buscar = () => {
    const token = sessao.tokenAtual();
    return fetch(montarUrl(caminho, query), {
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  };
  let resposta = await buscar();
  if (resposta.status === 401 && (await sessao.renovar())) resposta = await buscar();
  if (!resposta.ok) {
    await interpretar(resposta, problemDetailsSchema);
    throw new Error('Resposta inesperada do servidor');
  }
  const nome =
    /filename="([^"]+)"/.exec(resposta.headers.get('Content-Disposition') ?? '')?.[1] ?? null;
  return { blob: await resposta.blob(), nome };
}

/** Baixa um arquivo autenticado (XML original, planilha) e entrega ao navegador como download. */
export async function baixarArquivo(
  caminho: string,
  nomePadrao: string,
  query: Record<string, ValorQuery> = {},
): Promise<void> {
  const { blob, nome } = await buscarArquivo(caminho, query);
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement('a'), {
    href: url,
    download: nome ?? nomePadrao,
  });
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Abre um arquivo autenticado (DANFE) em nova aba. A aba é aberta JÁ no clique — depois do await
 * o navegador bloquearia como pop-up — e recebe o blob quando ele chega; em erro, fecha.
 */
export async function abrirArquivo(caminho: string): Promise<void> {
  const aba = window.open('', '_blank');
  try {
    const { blob } = await buscarArquivo(caminho);
    const url = URL.createObjectURL(blob);
    if (aba) aba.location.href = url;
    else window.open(url, '_blank');
  } catch (erro) {
    aba?.close();
    throw erro;
  }
}
